<template>
  <div class="status-overlay" :class="`status-overlay--${tone}`" role="alertdialog" aria-modal="true" :aria-label="title">
    <div class="status-overlay__card cb-rise-in">
      <div class="status-overlay__icon">
        <span v-if="spinner" class="cb-spinner status-overlay__spinner" aria-hidden="true"></span>
        <AppIcon v-else :name="icon" :size="34" />
      </div>
      <h2 class="status-overlay__title">{{ title }}</h2>
      <p v-if="text" class="status-overlay__text">{{ text }}</p>
      <div v-if="autoMs > 0" class="status-overlay__auto">
        <span class="status-overlay__auto-fill" :style="{ animationDuration: `${autoMs}ms` }"></span>
      </div>
      <div v-if="actions.length" class="status-overlay__actions">
        <AppButton
          v-for="action in actions"
          :key="action.id"
          :variant="action.variant || 'primary'"
          :size="action.size || 'md'"
          :icon="action.icon || ''"
          block
          @click="emit('action', action.id)"
        >
          {{ action.label }}
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * GameStatusOverlay — полноэкранный оверлей экрана игры (соперник ушёл, проблемы со связью/камерой,
 * ошибка загрузки модели).
 *
 * <GameStatusOverlay icon="warning" title="Соперник покинул игру" text="…" :auto-ms="4000"
 *   :actions="[{ id: 'home', label: 'На главную' }]" @action="onAction" />
 *
 * props: icon (AppIcon), title, text, tone ('neutral'|'danger'|'lime'), spinner, autoMs (полоска
 *        автоперехода), actions ([{id, label, variant?, size?, icon?}]). emits: action(id).
 */
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";

defineProps({
  icon: { type: String, default: "warning" },
  title: { type: String, required: true },
  text: { type: String, default: "" },
  tone: { type: String, default: "neutral", validator: (v) => ["neutral", "danger", "lime"].includes(v) },
  spinner: { type: Boolean, default: false },
  autoMs: { type: Number, default: 0 },
  actions: { type: Array, default: () => [] },
});

const emit = defineEmits(["action"]);
</script>

<style scoped>
.status-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--cb-z-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  padding:
    calc(var(--cb-safe-top) + 24px)
    calc(var(--cb-safe-right) + var(--cb-gutter))
    calc(var(--cb-safe-bottom) + 24px)
    calc(var(--cb-safe-left) + var(--cb-gutter));
  background: rgba(5, 7, 5, 0.78);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.status-overlay__card {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px 20px 20px;
  border-radius: var(--cb-radius-xl);
  text-align: center;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 40%),
    linear-gradient(180deg, var(--cb-surface-2) 0%, var(--cb-surface) 100%);
  border: 1px solid var(--cb-border-strong);
  box-shadow: var(--cb-shadow-pop), var(--cb-inner-highlight);
}

.status-overlay__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin-bottom: 4px;
  border-radius: 50%;
  background: var(--cb-surface-3);
  border: 1px solid var(--cb-border-strong);
  color: var(--cb-text);
}

.status-overlay--lime .status-overlay__icon {
  background: var(--cb-lime-soft);
  border-color: var(--cb-lime-border);
  color: var(--cb-lime);
  box-shadow: var(--cb-glow-sm);
}

.status-overlay--danger .status-overlay__icon {
  background: var(--cb-danger-soft);
  border-color: rgba(255, 107, 90, 0.5);
  color: var(--cb-danger);
}

.status-overlay__spinner {
  font-size: 34px;
}

.status-overlay__title {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.2;
}

.status-overlay__text {
  color: var(--cb-text-2);
  font-size: 15px;
  line-height: 1.45;
}

.status-overlay__auto {
  position: relative;
  width: 60%;
  height: 4px;
  margin-top: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.09);
}

.status-overlay__auto-fill {
  position: absolute;
  inset: 0 auto 0 0;
  width: 100%;
  border-radius: inherit;
  background: var(--cb-lime);
  box-shadow: 0 0 10px rgba(221, 245, 90, 0.6);
  transform-origin: left center;
  animation-name: status-overlay-auto;
  animation-timing-function: linear;
  animation-fill-mode: both;
}

.status-overlay__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-top: 10px;
}

@keyframes status-overlay-auto {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
</style>
